/** @typedef {import('react')} JSX */
import { type JSX } from 'react';
import styles from './Flags.module.scss';

type SizeOption = 'X-Small' | 'Small' | 'Medium' | 'Large' | 'X-Large';
type ShapeOption = 'Circle' | 'Square' | 'Rectangle';

interface ImageAsset {
  name: string;
  path: string;
}

// Dynamically import all icons from a folder
const imageModules = import.meta.glob(
  '../../../assets/Flags/*.{png,jpg,jpeg,svg}',
  {
    eager: true,
    import: 'default',
  }
);

const images: ImageAsset[] = Object.entries(imageModules).map(([path, mod]: [string, unknown]) => {
  const name = path.replace(/^.*[\\/]/, '').replace(/\.\w+$/, '');
  return { name, path: mod as string };
});
/**
 * FlagsProps defines the properties for the Flags component.
 */
export interface FlagsProps {
  selectedCountryName?: string;
  size?: SizeOption;
  shape?: ShapeOption;
}

const sizeClassMap: Record<SizeOption, string> = {
  'X-Small': 'flag-xsmall',
  Small: 'flag-small',
  Medium: 'flag-medium',
  Large: 'flag-large',
  'X-Large': 'flag-xlarge',
};

const shapeClassMap: Record<ShapeOption, string> = {
  Circle: 'flag-circle',
  Square: 'flag-square',
  Rectangle: 'flag-round',
};

/**
 * Custom Flag component that renders a flag icon for the selected country. It displays the flag in a customizable size and shape.
 *
 * ## Import path
 * ```tsx
 * import { Flags } from '@kpmg-global-audit-emu/react-ui-kit/atoms/Flags';
 * ```
 * @param {object} root0 - Props for the Flags component.
 * @param {string} root0.selectedCountryName - Name of the selected country (used to match the flag image).
 * @param {SizeOption} root0.size - Size of the flag icon (e.g., Small, Medium, Large).
 * @param {ShapeOption} root0.shape - Shape of the flag icon (e.g., Circle, Square, Rectangle).
 * @returns {JSX.Element} The rendered flag component.
 */
export const Flags = ({
  selectedCountryName,
  size = 'Medium',
  shape = 'Rectangle',
}: FlagsProps): JSX.Element => {
  const selectedCountry = selectedCountryName
    ? images.find((img) => img.name === selectedCountryName)?.path
    : null;
  return (
    <div className={styles[`flag-container`]}>
      <div className={styles[`flag-wrapper`]}>
        {selectedCountry ? (
          <img
            src={selectedCountry}
            alt={selectedCountryName}
            className={`${styles.flag} ${styles[sizeClassMap[size]]} ${styles[shapeClassMap[shape]]} ${styles.icon}`}
          />
        ) : (
          <span style={{ fontSize: '20px', color: 'gray' }}>No flag selected</span>
        )}
      </div>
    </div>
  );
};
